import React, { KeyboardEvent, useEffect, useState } from "react";
import "./AutocompleteInput.css";

export interface ListItem {
  id: string | number;
  name: string;
}

interface AutocompleteInputProps<T extends ListItem> {
  items: T[];
  value: T | null;
  onChange: (selected: T | null) => void;
  placeholder?: string;
  showAllOnFocus?: boolean; // 🆕 Feature flag
}

function AutocompleteInput<T extends ListItem>({
  items,
  value,
  onChange,
  placeholder = "Search...",
  showAllOnFocus = false, // default is off
}: AutocompleteInputProps<T>) {
  const [inputValue, setInputValue] = useState("");
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    console.log("Items updated:", items);
  }, [items]);

  useEffect(() => {
    setInputValue(value ? value.name : "");
  }, [value]);

  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = items.filter((item) =>
        item.name.toLowerCase().startsWith(inputValue.toLowerCase())
      );
      setFilteredItems(filtered);
      setShowDropdown(filtered.length > 0);
    } else if (showAllOnFocus && document.activeElement === inputRef.current) {
      setFilteredItems(items);
      setShowDropdown(items.length > 0);
    } else {
      setFilteredItems([]);
      setShowDropdown(false);
    }
    setHighlightedIndex(-1);
  }, [inputValue, items, showAllOnFocus]);

  // Add a ref to track input focus
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => (prev + 1) % filteredItems.length);
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) =>
        prev <= 0 ? filteredItems.length - 1 : prev - 1
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      handleSelect(filteredItems[highlightedIndex]);
    }
  };

  const handleSelect = (item: T) => {
    onChange(item);
    setInputValue(item.name);
    setShowDropdown(false);
  };

  const handleFocus = () => {
    if (showAllOnFocus) {
      // Always filter fresh
      setFilteredItems([...items]); // ensure it's a new array
      setShowDropdown(items.length > 0);
    } else {
      const filtered = items.filter((item) =>
        item.name.toLowerCase().startsWith(inputValue.toLowerCase())
      );
      setFilteredItems(filtered);
      setShowDropdown(filtered.length > 0);
    }
  };

  return (
    <div className="autocomplete-container">
      <input
        ref={inputRef}
        type="text"
        className="form-control"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {showDropdown && (
        <ul className="autocomplete-dropdown list-group">
          {filteredItems.map((item, idx) => (
            <li
              key={item.id}
              className={`list-group-item${
                highlightedIndex === idx ? " active" : ""
              }`}
              onMouseDown={() => handleSelect(item)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {item.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AutocompleteInput;
